/**
 * ChainStrip — inline 7-link chain rendered inside the Chain
 * column on each Threat row. Replaces the legacy "count columns"
 * (#Damages | #Atk | #Goals | #Reqs ...) with a single dense
 * horizontal strip:
 *
 *   DAMAGE ▸ ATTACK ▸ RISK ▸ GOAL  ▸ CONTROL ▸ REQ
 *                              ↘ CLAIM
 *
 * Each link is a small chip showing label + count + composition
 * breakdown. Empty links render with dashed border + "—".
 *
 * Every colour + label decision routes through the centralised
 * accessors in `utils/severityStyle.js` (Step 1) — no hex literals,
 * no inline label capitalisation.
 */
import React from 'react';
import {
  getEntityTypeColor,
  getImpactColor,
  getImpactLabel,
  getFeasibilityColor,
  getFeasibilityLabel,
  getTreatmentDecisionColor,
  getTreatmentDecisionLabel,
  getCALColor,
  getCALLabel,
} from '../../../utils/severityStyle';
import styles from './ChainStrip.module.css';

/** Count how often each value appears in items[*][key], preserving
 *  the canonical order supplied in `canonicalOrder`. Returns an
 *  array of { key, count } objects. */
function tallyBy(items, key, canonicalOrder) {
  const counts = new Map();
  items.forEach((item) => {
    const v = item?.[key];
    if (v == null) return;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  });
  const out = [];
  canonicalOrder.forEach((v) => {
    if (counts.has(v)) out.push({ key: v, count: counts.get(v) });
  });
  // Append unknown values at the end (defensive — backend should
  // only ever emit canonical enum values, but if a new one arrives
  // we still surface its count instead of silently dropping it).
  counts.forEach((count, v) => {
    if (!canonicalOrder.includes(v)) out.push({ key: v, count });
  });
  return out;
}

const IMPACT_ORDER      = ['Severe', 'Major', 'Moderate', 'Minor', 'Negligible'];
const FEASIBILITY_ORDER = ['High', 'Medium', 'Low', 'Very Low'];
const TREATMENT_ORDER   = ['Avoid', 'Reduce', 'Share', 'Retain'];
const CAL_ORDER         = ['CAL4', 'CAL3', 'CAL2', 'CAL1'];

/** Single chain-link chip. `kind` drives the border colour via
 *  the entity-type palette; `value` drives the body content
 *  (numeric → bold monospace count; string → regular text — used
 *  by the Threat and Asset columns to read the row's anchor ids as
 *  chain-link chips). Empty value (null / '' / 0) renders dashed.
 *  Exported so the Table consumer can reuse this primitive for the
 *  Threat + Asset columns. */
export const ChainLink = ({ kind, label, value, composition }) => {
  const color = getEntityTypeColor(kind);
  const isNumeric = typeof value === 'number';
  const isEmpty = value == null || value === '' || (isNumeric && value === 0);
  if (isEmpty) {
    return (
      <div className={`${styles.link} ${styles.linkEmpty}`}>
        <span className={styles.linkLabel}>{label}</span>
        <span className={styles.linkDash} aria-hidden="true">—</span>
      </div>
    );
  }
  return (
    <div
      className={styles.link}
      style={{
        borderColor: `color-mix(in srgb, ${color} 30%, transparent)`,
        background: `color-mix(in srgb, ${color} 6%, transparent)`,
      }}
    >
      <div className={styles.linkHead}>
        <span className={styles.linkLabel} style={{ color }}>{label}</span>
        <span className={isNumeric ? styles.linkCount : styles.linkValue} title={isNumeric ? undefined : String(value)}>
          {value}
        </span>
      </div>
      {composition && composition.length > 0 && (
        <div className={styles.linkComposition}>{composition}</div>
      )}
    </div>
  );
};

const Arrow = () => <span className={styles.arrow} aria-hidden="true">▸</span>;

/** Tiny inline composition chip (rendered inside a ChainLink's
 *  body) — count · short-label, coloured per the family palette. */
const CompChip = ({ count, label, color, title }) => (
  <span
    className={styles.compChip}
    style={{ color, borderColor: `color-mix(in srgb, ${color} 30%, transparent)` }}
    title={title}
  >
    <span className={styles.compCount}>{count}</span>
    <span className={styles.compLabel}>{label}</span>
  </span>
);

const ChainStrip = ({ row }) => {
  const { damages, attackPaths, risks, goals, claims, controls, requirements } = row;

  // Damage composition — group by impact rating.
  const damageComp = tallyBy(damages, 'final_impact_rating', IMPACT_ORDER).map(({ key, count }) => (
    <CompChip
      key={key}
      count={count}
      label={getImpactLabel(key)}
      color={getImpactColor(key)}
      title={`${count} ${getImpactLabel(key)}`}
    />
  ));

  // Attack composition — group by feasibility rating.
  const attackComp = tallyBy(attackPaths, 'attack_feasibility_rating', FEASIBILITY_ORDER).map(({ key, count }) => (
    <CompChip
      key={key}
      count={count}
      label={getFeasibilityLabel(key)}
      color={getFeasibilityColor(key)}
      title={`${count} ${getFeasibilityLabel(key)}`}
    />
  ));

  // Risk composition — group by treatment_decision (standard scheme
  // for the 2.0 table; the map uses the `map` scheme on its edges).
  const riskComp = tallyBy(risks, 'treatment_decision', TREATMENT_ORDER).map(({ key, count }) => (
    <CompChip
      key={key}
      count={count}
      label={getTreatmentDecisionLabel(key)}
      color={getTreatmentDecisionColor(key)}
      title={`${count} ${getTreatmentDecisionLabel(key)}`}
    />
  ));

  // Requirement composition — group by CAL level.
  const reqComp = tallyBy(requirements, 'cal_level', CAL_ORDER).map(({ key, count }) => (
    <CompChip
      key={key}
      count={count}
      label={getCALLabel(key)}
      color={getCALColor(key)}
      title={`${count} ${getCALLabel(key)}`}
    />
  ));

  return (
    <div className={styles.chainStrip}>
      <ChainLink kind="damage"      label="DAMAGE"  value={damages.length}      composition={damageComp} />
      <Arrow />
      <ChainLink kind="attack"      label="ATTACK"  value={attackPaths.length}  composition={attackComp} />
      <Arrow />
      <ChainLink kind="risk"        label="RISK"    value={risks.length}        composition={riskComp} />
      <Arrow />
      <ChainLink kind="goal"        label="GOAL"    value={goals.length} />
      {claims.length > 0 && (
        <>
          <span className={styles.branchArrow} aria-hidden="true">↘</span>
          <ChainLink kind="claim"   label="CLAIM"   value={claims.length} />
        </>
      )}
      <Arrow />
      <ChainLink kind="control"     label="CONTROL" value={controls.length} />
      <Arrow />
      <ChainLink kind="requirement" label="REQ"     value={requirements.length} composition={reqComp} />
    </div>
  );
};

export default ChainStrip;
